import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ScreenHeader, Surface, Button, IconButton, Badge, Avatar, Divider, Icon, FeedbackMsg, inputCss, Dialog } from '../components/ui';

const API_URL = process.env.REACT_APP_API_URL;

function formatarTelefone(valor) {
  valor = valor.replace(/\D/g, '');
  if (valor.length > 11) valor = valor.slice(0, 11);
  if (valor.length > 2) valor = `(${valor.slice(0, 2)})${valor.slice(2)}`;
  if (valor.length > 9) valor = valor.replace(/^(\(\d{2}\))(\d{5})(\d{4}).*/, '$1$2-$3');
  else if (valor.length > 6) valor = valor.replace(/^(\(\d{2}\))(\d{4})(\d{0,4})/, '$1$2-$3');
  return valor;
}

const selectStyle = { height: 40, padding: '0 12px', fontSize: 14, width: '100%', background: 'var(--bg-sunken)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', outline: 'none' };

function UsuarioModal({ modalTipo, usuario, onClose, onSaved, isProfessora }) {
  const isCreate = modalTipo === 'criar';
  const isEdit = modalTipo === 'editar';
  const isDetails = modalTipo === 'detalhes';
  const isSenha = modalTipo === 'senha';

  const [form, setForm] = useState({
    nome: usuario?.nome || '', telefone: usuario?.telefone || '', endereco: usuario?.endereco || '',
    login: usuario?.login || '', email: usuario?.email || '', senha: '',
    valor_hora: usuario?.valor_hora || '',
    valor_hora_particular: usuario?.valor_hora_particular ?? '',
    valor_hora_grupo: usuario?.valor_hora_grupo ?? '',
    tipo: usuario?.tipo || 'aluno',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  function upd(name, value) { setForm(f => ({ ...f, [name]: value })); }

  function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    const dados = { ...form };
    if (!isProfessora || dados.tipo !== 'aluno') delete dados.valor_hora;
    const req = isCreate
      ? axios.post(`${API_URL}/alunos`, dados, { withCredentials: true })
      : isEdit
      ? axios.put(`${API_URL}/alunos/${usuario.id}`, dados, { withCredentials: true })
      : axios.post(`${API_URL}/alunos/${usuario.id}/senha`, { senha: form.senha }, { withCredentials: true });
    req.then(() => { onSaved(); onClose(); })
      .catch(err => setMsg(err.response?.data?.erro || 'Erro.'))
      .finally(() => setSaving(false));
  }

  const title = isCreate ? 'Novo usuário' : isEdit ? `Editar — ${usuario.nome}` : isDetails ? `Detalhes — ${usuario.nome}` : `Senha — ${usuario.nome}`;

  if (isDetails) return (
    <Dialog title={title} onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-3)', marginBottom: 'var(--gap-3)' }}>
        <Avatar name={usuario.nome} size={52}/>
        <div>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-primary)' }}>{usuario.nome}</div>
          <Badge tone={usuario.tipo === 'professora' ? 'accent' : 'neutral'} style={{ marginTop: 4 }}>
            {usuario.tipo}
          </Badge>
        </div>
      </div>
      <Surface>
        {[
          { label: 'E-mail', value: usuario.email },
          { label: 'Login', value: usuario.login, mono: true },
          { label: 'Telefone', value: usuario.telefone, mono: true },
          { label: 'Endereço', value: usuario.endereco },
          isProfessora && usuario.tipo === 'aluno' && usuario.valor_hora_particular != null && { label: 'Valor particular/h', value: `R$ ${Number(usuario.valor_hora_particular).toFixed(2)}`, mono: true },
          isProfessora && usuario.tipo === 'aluno' && usuario.valor_hora_grupo != null && { label: 'Valor grupo/h', value: `R$ ${Number(usuario.valor_hora_grupo).toFixed(2)}`, mono: true },
          isProfessora && usuario.tipo === 'aluno' && usuario.valor_hora_particular == null && usuario.valor_hora_grupo == null && { label: 'Valor/hora', value: `R$ ${Number(usuario.valor_hora || 0).toFixed(2)}`, mono: true },
        ].filter(Boolean).map((f, i) => (
          <React.Fragment key={i}>
            {i > 0 && <Divider/>}
            <div style={{ padding: 'var(--pad-card)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 2 }}>{f.label}</div>
              <div style={{ fontSize: 13.5, color: 'var(--text-primary)', fontFamily: f.mono ? '"Geist Mono", monospace' : 'inherit' }}>{f.value || '—'}</div>
            </div>
          </React.Fragment>
        ))}
      </Surface>
    </Dialog>
  );

  const formFields = isSenha
    ? [{ name: 'senha', label: 'Nova senha', type: 'password' }]
    : [
        { name: 'nome', label: 'Nome completo', type: 'text', required: true },
        { name: 'email', label: 'E-mail (opcional)', type: 'email', required: false },
        { name: 'telefone', label: 'Telefone', type: 'tel', required: true },
        { name: 'endereco', label: 'Endereço', type: 'text', required: true },
        { name: 'login', label: 'Login', type: 'text', required: true },
        { name: 'senha', label: isCreate ? 'Senha' : 'Nova senha (em branco = manter)', type: 'password', required: isCreate },
      ];

  return (
    <Dialog title={title} onClose={onClose}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-2)' }}>
        {formFields.map(f => (
          <div key={f.name} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>{f.label}</label>
            <input name={f.name} type={f.type} required={f.required} value={form[f.name]}
              autoComplete={f.name === 'senha' ? 'new-password' : undefined}
              onChange={e => upd(f.name, f.name === 'telefone' ? formatarTelefone(e.target.value) : e.target.value)}
              style={inputCss}/>
          </div>
        ))}
        {(isCreate || isEdit) && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Tipo</label>
              <select value={form.tipo} onChange={e => upd('tipo', e.target.value)} style={selectStyle}>
                <option value="aluno">Aluno</option>
                <option value="professora">Professora</option>
              </select>
            </div>
            {isProfessora && form.tipo === 'aluno' && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Valor aula particular (R$)</label>
                  <input type="number" step="0.01" min={0} value={form.valor_hora_particular}
                    onChange={e => upd('valor_hora_particular', e.target.value)} style={inputCss}/>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Valor aula em grupo (R$)</label>
                  <input type="number" step="0.01" min={0} value={form.valor_hora_grupo}
                    onChange={e => upd('valor_hora_grupo', e.target.value)} style={inputCss}/>
                </div>
              </>
            )}
          </>
        )}
        {msg && <FeedbackMsg msg={msg}/>}
        <Button variant="primary" size="lg" disabled={saving} style={{ width: '100%', marginTop: 4 }}>
          {saving ? 'Salvando…' : isSenha ? 'Alterar senha' : isCreate ? 'Criar usuário' : 'Salvar alterações'}
        </Button>
      </form>
    </Dialog>
  );
}

export default function Usuarios() {
  const { tipo } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [modal, setModal] = useState(null);

  function carregar() {
    axios.get(`${API_URL}/usuarios`, { withCredentials: true })
      .then(r => setUsuarios(r.data)).catch(() => setUsuarios([]));
  }

  useEffect(() => { carregar(); }, []);

  const filtrados = usuarios
    .filter(u =>
      (u.nome?.toLowerCase().includes(busca.toLowerCase()) ||
       u.email?.toLowerCase().includes(busca.toLowerCase()) ||
       u.login?.toLowerCase().includes(busca.toLowerCase())) &&
      (!filtroTipo || u.tipo === filtroTipo)
    )
    .sort((a, b) => a.nome.localeCompare(b.nome));

  function excluir(id) {
    if (!window.confirm('Excluir este usuário?')) return;
    axios.delete(`${API_URL}/alunos/${id}`, { withCredentials: true }).then(carregar);
  }

  const selectStyle2 = { height: 36, padding: '0 10px', fontSize: 13, background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', outline: 'none' };

  return (
    <div className="mema-screen" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)', paddingBottom: 90 }}>
      <ScreenHeader title="Usuários" action={
        <Button size="sm" variant="primary" icon="plus" onClick={() => setModal({ tipo: 'criar', usuario: null })}>Novo</Button>
      }/>

      <div style={{ padding: '0 var(--gap-3)', display: 'flex', gap: 'var(--gap-2)' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-md)' }}>
          <Icon name="search" size={15} style={{ color: 'var(--text-tertiary)' }}/>
          <input type="text" placeholder="Buscar usuário…" value={busca} onChange={e => setBusca(e.target.value)}
            style={{ flex: 1, border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}/>
        </div>
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={selectStyle2}>
          <option value="">Todos</option>
          <option value="aluno">Alunos</option>
          <option value="professora">Professoras</option>
        </select>
      </div>

      <div style={{ padding: '0 var(--gap-3)' }}>
        {filtrados.length === 0 ? (
          <Surface padded style={{ textAlign: 'center', padding: 'var(--gap-4)' }}>
            <Icon name="users" size={28} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.35, color: 'var(--text-tertiary)' }}/>
            <div style={{ fontSize: 14, fontWeight: 540, color: 'var(--text-primary)' }}>Nenhum usuário encontrado.</div>
          </Surface>
        ) : (
          <Surface>
            {filtrados.map((u, i) => (
              <React.Fragment key={u.id}>
                {i > 0 && <Divider/>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-2)', padding: 'var(--pad-card)' }}>
                  {u.foto_url
                    ? <img src={u.foto_url} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}/>
                    : <Avatar name={u.nome} size={40}/>
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 540, color: 'var(--text-primary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{u.nome}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 140 }}>{u.email}</span>
                      <Badge tone={u.tipo === 'professora' ? 'accent' : 'neutral'}>{u.tipo}</Badge>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <IconButton icon="user" size={28} variant="ghost" onClick={() => setModal({ tipo: 'detalhes', usuario: u })} label="Detalhes"/>
                    <IconButton icon="edit" size={28} variant="ghost" onClick={() => setModal({ tipo: 'editar', usuario: u })} label="Editar"/>
                    <IconButton icon="settings" size={28} variant="ghost" onClick={() => setModal({ tipo: 'senha', usuario: u })} label="Senha"/>
                    <IconButton icon="close" size={28} variant="ghost" style={{ color: 'var(--danger)' }} onClick={() => excluir(u.id)} label="Excluir"/>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </Surface>
        )}
      </div>

      {modal && (
        <UsuarioModal
          modalTipo={modal.tipo} usuario={modal.usuario}
          isProfessora={tipo === 'professora'}
          onClose={() => setModal(null)} onSaved={carregar}
        />
      )}
    </div>
  );
}
